/* HYPOGEN set advantage, installation draft 2026-10-25.
 * Read-only Storefront API. No cart writes, no tracking, no discount prediction.
 * API version must be reviewed before its support window expires.
 */
(() => {
  if (customElements.get('hypogen-set-advantage')) return;
  const requests = new Map();
  const version = '2026-10';
  function decimal(value) {
    const match = /^(\d+)(?:\.(\d+))?$/.exec(String(value));
    if (!match) throw new Error('Invalid money amount');
    return { digits: BigInt(match[1] + (match[2] || '')), scale: (match[2] || '').length };
  }
   function units(value, scale) {
    const parsed = decimal(value);
    return parsed.digits * 10n ** BigInt(scale - parsed.scale);
  }

  function formatNativeMoney(saving, scale, quantity, config) {
    function decode(value) {
      if (typeof value !== 'string') {
        throw new Error('Missing native money sample');
      }
      const textarea = document.createElement('textarea');
      textarea.innerHTML = value;
      return textarea.value;
    }

    const numberSample = decode(config.nativeNumberSample).trim();
    const moneySample = decode(config.nativeMoneySample);
    const pattern = /^1(?<group>[^0-9]*)234\k<group>567(?:(?<decimal>[.,])00)?$/;
    const match = pattern.exec(numberSample);

    if (!match) {
      throw new Error('Unsupported native number sample');
    }

    const position = moneySample.indexOf(numberSample);
    if (
      position < 0 ||
      position !== moneySample.lastIndexOf(numberSample)
    ) {
      throw new Error('Inconsistent native money samples');
    }

    const places = match.groups.decimal ? 2 : 0;
    let rounded = saving * BigInt(quantity);

    if (scale > places) {
      const divisor = 10n ** BigInt(scale - places);
      rounded = (rounded + divisor / 2n) / divisor;
    } else {
      rounded *= 10n ** BigInt(places - scale);
    }

    const digits = rounded.toString().padStart(places + 1, '0');
    const integer = places ? digits.slice(0, -places) : digits;
    const grouped = integer.replace(
      /\B(?=(\d{3})+(?!\d))/g,
      () => match.groups.group
    );
    const number = places
      ? grouped + match.groups.decimal + digits.slice(-places)
      : grouped;

    return (
      moneySample.slice(0, position) +
      number +
      moneySample.slice(position + numberSample.length)
    );
  }
  function calculate(variant, currency) {
    const components = variant.components;
    if (!components || components.pageInfo.hasNextPage || !components.nodes.length) throw new Error('Incomplete components');
    const prices = [variant.price, ...components.nodes.map(item => item.productVariant?.price)];
    if (prices.some(price => !price || price.currencyCode !== currency)) throw new Error('Currency mismatch');
    const scale = Math.max(...prices.map(price => decimal(price.amount).scale));
    const set = units(variant.price.amount, scale);
    let sum = 0n;
    for (const item of components.nodes) {
      if (!Number.isSafeInteger(item.quantity) || item.quantity <= 0) throw new Error('Invalid component quantity');
      sum += BigInt(item.quantity) * units(item.productVariant.price.amount, scale);
    }
    return { set, sum, saving: sum - set, scale };
  }
  function badgeIsValid(text, saving, sum) {
    const match = /^\s*(>)?\s*(\d+(?:[.,]\d+)?)\s*%?\s*$/.exec(text);
    if (!match || sum <= 0n || saving <= 0n) return false;
    const percentage = decimal(match[2].replace(',', '.'));
    const left = saving * 100n * 10n ** BigInt(percentage.scale);
    const right = sum * percentage.digits;
    return match[1] ? left > right : left === right;
  }
  function readVariant(config, id) {
    const key = [config.country, config.language, config.currency, id].join(':');
    if (!requests.has(key)) {
      const query = `query Advantage($id: ID!, $country: CountryCode!, $language: LanguageCode!) @inContext(country: $country, language: $language) { node(id: $id) { ... on ProductVariant { id price { amount currencyCode } components(first: 100) { nodes { quantity productVariant { id price { amount currencyCode } } } pageInfo { hasNextPage } } } } }`;
      const promise = fetch(`/api/${version}/graphql.json`, {
        method: 'POST',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ query, variables: { id: `gid://shopify/ProductVariant/${id}`, country: config.country, language: config.language } }),
        signal: AbortSignal.timeout(10000)
      }).then(async response => {
        if (!response.ok) throw new Error('Price request failed');
        const result = await response.json();
        if (result.errors?.length || !result.data?.node) throw new Error('Price data unavailable');
        return result.data.node;
      }).catch(error => { requests.delete(key); throw error; });
      requests.set(key, promise);
    }
    return requests.get(key);
  }
  class SetAdvantage extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      try { this.config = JSON.parse(this.querySelector('[data-advantage-config]').textContent); }
      catch { return; }
      this.root = this.closest('product-info');
      if (!this.root) return;
      this.onChange = () => this.refresh();
      this.root.addEventListener('change', this.onChange);
      this.root.addEventListener('input', this.onChange);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, event => {
          if (String(event.data?.sectionId) === String(this.config.sectionId)) this.refresh();
        });
        this.unsubscribeQuantity = subscribe(PUB_SUB_EVENTS.quantityUpdate, () => this.refresh());
      }
      this.refresh();
    }
    disconnectedCallback() {
      this.connected = false;
      this.sequence = (this.sequence || 0) + 1;
      this.root?.removeEventListener('change', this.onChange);
      this.root?.removeEventListener('input', this.onChange);
      this.unsubscribe?.();
      this.unsubscribeQuantity?.();
    }
    clear() {
      this.querySelectorAll('[data-advantage-saving]').forEach(element => { element.hidden = true; });
      const badge = this.querySelector('[data-advantage-badge]');
      if (badge) badge.textContent = this.config.neutral;
    }
    async refresh() {
      const sequence = this.sequence = (this.sequence || 0) + 1;
      this.clear();
      const config = this.config;
      if (config.b2b || !config.country || !config.currency || !config.language) return;
      const field = this.root.querySelector(`#product-form-${CSS.escape(String(config.sectionId))} [name="id"]`);
      const variantId = config.mode === 'upsell' ? String(config.variantId) : String(field?.value || config.variantId);
      const quantityField = this.root.querySelector('input[name="quantity"]');
      const quantity = config.mode === 'upsell' ? 1 : Number(quantityField?.value || 1);
      if (!Number.isSafeInteger(quantity) || quantity < 1) return;
      try {
        const variant = await readVariant(config, variantId);
        const totals = calculate(variant, config.currency);
        const expected = config.prices[String(variantId)];
        // Liquid prices use the theme's money units; never replace the displayed set price.
        if (expected === undefined || Math.abs(Number(variant.price.amount) - Number(expected) / 100) > 0.000001) return;
        if (!this.isConnected || sequence !== this.sequence || totals.saving <= 0n) return;
                const formatted = formatNativeMoney(
          totals.saving,
          totals.scale,
          quantity,
          config
        );
        this.querySelectorAll('[data-advantage-amount]').forEach(element => { element.textContent = formatted; });
        this.querySelectorAll('[data-advantage-saving]').forEach(element => { element.hidden = false; });
        const badge = this.querySelector('[data-advantage-badge]');
        if (badge && badgeIsValid(config.badge, totals.saving, totals.sum)) badge.textContent = config.save.replace('[percentage]', config.badge.trim().replace(/%/g, '') + '%');
      } catch {
        // Fail closed: never show a guessed advantage or a stale percentage.
      }
    }
  }
  customElements.define('hypogen-set-advantage', SetAdvantage);
})();
